import { useEffect, useState, useCallback } from "react";
import type {
  ZudokuPlugin,
  ProfileNavigationItem,
  ZudokuContext,
  ApiIdentity,
} from "zudoku";
import { useAuth } from "zudoku/hooks";
import { Link } from "zudoku/components";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const _injectedGql =
  typeof window !== "undefined" ? (window as any).__DOCS_GRAPHQL_URL__ : undefined;
const GRAPHQL_API_URL =
  typeof _injectedGql === "string" && _injectedGql.startsWith("http")
    ? _injectedGql
    : "http://localhost:4000/graph";

const _injectedSignup =
  typeof window !== "undefined" ? (window as any).__DOCS_DASHBOARD_SIGNUP_URL__ : undefined;
const DASHBOARD_SIGNUP_URL =
  typeof _injectedSignup === "string" && _injectedSignup.startsWith("http")
    ? _injectedSignup
    : "https://dashboard.payonify.co.zw/join";

const STORAGE = {
  AUTH: "payonify-auth",
  KEYS: "payonify-playground-keys",
  PROJECT: "payonify-project",
  ZUDOKU_AUTH: "auth-state",
} as const;

// ---------------------------------------------------------------------------
// GraphQL queries
// ---------------------------------------------------------------------------
const LOGIN_MUTATION = `
  mutation ($email: String!, $password: String!) {
    accounts {
      login(email: $email, password: $password) {
        errors { key message }
        otpauthUri
        token
        user { id email firstName lastName avatar }
      }
    }
  }
`;

const TWO_FA_LOGIN_MUTATION = `
  mutation ($userId: ID!, $otpCode: String!) {
    accounts {
      twoFaLogin(userId: $userId, otpCode: $otpCode) {
        errors { key message }
        token
        user { id email firstName lastName avatar }
      }
    }
  }
`;

const PROJECTS_QUERY = `
  query {
    projects {
      stagingProjects(pagination: { first: 100 }) {
        entries {
          name
          identifier
        }
      }
    }
  }
`;

const PUBLISHABLE_KEY_QUERY = `
  query {
    auth {
      publishableKey {
        token
        tokenType
        status
      }
    }
  }
`;

const SECRET_KEY_QUERY = `
  query ($publishableKey: String!) {
    auth {
      secretKey(publishableKey: $publishableKey) {
        token
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface StoredAuth {
  token: string;
  email: string;
  firstName: string;
  avatar?: string;
}

interface StoredKeys {
  apiKey: string;
  appId: string;
}

interface StoredProject {
  identifier: string;
  name: string;
}

interface Project {
  name: string;
  identifier: string;
}

// ---------------------------------------------------------------------------
// LocalStorage helpers
// ---------------------------------------------------------------------------
function getStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setStored(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
}

function getStoredAuth(): StoredAuth | null {
  return getStored<StoredAuth>(STORAGE.AUTH);
}

function getStoredKeys(): StoredKeys | null {
  const stored = getStored<StoredKeys>(STORAGE.KEYS);
  if (stored?.apiKey && stored?.appId) return stored;
  return null;
}

function getStoredProject(): StoredProject | null {
  return getStored<StoredProject>(STORAGE.PROJECT);
}

function clearAllStorage() {
  localStorage.removeItem(STORAGE.AUTH);
  localStorage.removeItem(STORAGE.KEYS);
  localStorage.removeItem(STORAGE.PROJECT);
}

// ---------------------------------------------------------------------------
// GraphQL fetch helper
// ---------------------------------------------------------------------------
async function gqlFetch<T = unknown>(
  query: string,
  variables?: Record<string, unknown>,
  extraHeaders?: Record<string, string>,
): Promise<{ data?: T; errors?: Array<{ message: string }> }> {
  const res = await fetch(GRAPHQL_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...extraHeaders,
    },
    body: JSON.stringify({ query, variables }),
  });
  return res.json();
}

// ---------------------------------------------------------------------------
// Zudoku auth-state localStorage helper (for onPageLoad)
// ---------------------------------------------------------------------------
function syncAuthStateToLocalStorage(auth: StoredAuth) {
  try {
    const raw = localStorage.getItem(STORAGE.ZUDOKU_AUTH);
    const authState = raw ? JSON.parse(raw) : { version: 0 };
    // Always update profile data to avoid stale name/avatar
    authState.state = {
      isAuthenticated: true,
      isPending: false,
      profile: {
        sub: auth.email,
        email: auth.email,
        emailVerified: true,
        name: auth.firstName || auth.email,
        pictureUrl: auth.avatar || undefined,
      },
      providerData: null,
    };
    if (authState.version === undefined) authState.version = 0;
    localStorage.setItem(STORAGE.ZUDOKU_AUTH, JSON.stringify(authState));
  } catch {
    // ignore
  }
}

/** Dismiss Zudoku's built-in playground login dialog (Radix focus trap) */
function dismissPlaygroundDialog() {
  try {
    sessionStorage.setItem(
      "remember-skip-login",
      JSON.stringify({ state: { skipLogin: true }, version: 0 }),
    );
  } catch {}
}

/** Restore Zudoku's playground login dialog so it shows on next visit */
function restorePlaygroundDialog() {
  try {
    sessionStorage.removeItem("remember-skip-login");
  } catch {}
}

// ---------------------------------------------------------------------------
// LoginModalController — rendered via the "layout-after-head" slot so it's
// always mounted inside the Zudoku React tree and can use useAuth().
// ---------------------------------------------------------------------------
type Step = "login" | "otp" | "project" | "done";

export function LoginModalController() {
  const { setLoggedIn, setLoggedOut, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("login");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // OTP form
  const [otpCode, setOtpCode] = useState("");
  const [userId, setUserId] = useState("");

  // Project selection
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState("");

  // Disable Zudoku's Radix Dialog focus trap while our modal is open
  useEffect(() => {
    if (open) {
      document.querySelectorAll('[role="dialog"]').forEach((el) => {
        el.setAttribute("inert", "");
        el.previousElementSibling?.setAttribute("inert", "");
      });
    }
    return () => {
      document.querySelectorAll('[role="dialog"][inert]').forEach((el) => {
        el.removeAttribute("inert");
        el.previousElementSibling?.removeAttribute("inert");
      });
    };
  }, [open]);

  // Toggle body class for auth-aware CSS (e.g. disabling Send button)
  useEffect(() => {
    document.body.classList.toggle('pn-authenticated', isAuthenticated);
  }, [isAuthenticated]);

  // Always sync auth profile on mount to ensure fresh name/avatar.
  // onPageLoad() writes to localStorage but Zustand doesn't re-read after init,
  // so we must call setLoggedIn() to update the in-memory store.
  useEffect(() => {
    const auth = getStoredAuth();
    const keys = getStoredKeys();
    if (auth?.token && keys) {
      setLoggedIn({
        profile: {
          sub: auth.email,
          email: auth.email,
          emailVerified: true,
          name: auth.firstName || auth.email,
          pictureUrl: auth.avatar || undefined,
        },
        providerData: null,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When not authenticated, overlay the playground with a login prompt.
  // Zudoku's built-in dialog can be dismissed (skipLogin persists in Zustand),
  // so we add our own overlay that reappears every time the playground opens.
  useEffect(() => {
    if (isAuthenticated) return;

    function addOverlay() {
      // Target the playground panel (80vh height container)
      const panels = document.querySelectorAll('.h-\\[80vh\\]');
      panels.forEach((panel) => {
        if (panel.querySelector('.pn-playground-gate')) return;
        const el = panel as HTMLElement;
        el.style.position = 'relative';
        const overlay = document.createElement('div');
        overlay.className = 'pn-playground-gate';
        overlay.style.cssText =
          'position:absolute;inset:0;z-index:50;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;background:hsl(var(--background)/0.85);backdrop-filter:blur(4px);';
        overlay.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.5"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <p style="margin:0;font-size:14px;color:hsl(var(--muted-foreground))">Login to use the Playground</p>
          <button class="pn-gate-login" style="padding:6px 20px;font-size:13px;font-weight:500;border-radius:var(--radius);background:hsl(var(--primary));color:hsl(var(--primary-foreground));border:none;cursor:pointer">Login</button>
        `;
        overlay.querySelector('.pn-gate-login')?.addEventListener('click', () => {
          window.dispatchEvent(new CustomEvent('payonify:sign-in'));
        });
        el.appendChild(overlay);
      });
    }

    function removeOverlays() {
      document.querySelectorAll('.pn-playground-gate').forEach((el) => el.remove());
    }

    addOverlay();
    const observer = new MutationObserver(addOverlay);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { observer.disconnect(); removeOverlays(); };
  }, [isAuthenticated]);

  // Inject avatar into Zudoku's header dropdown button
  useEffect(() => {
    if (!isAuthenticated) return;
    const auth = getStoredAuth();
    const name = auth?.firstName || auth?.email || "";
    const avatarUrl = auth?.avatar;

    function injectAvatar() {
      const buttons = document.querySelectorAll("header button");
      buttons.forEach((btn) => {
        if (btn.textContent?.trim() !== name || btn.querySelector(".pn-avatar, img, .rounded-full")) return;
        const avatar = document.createElement("span");
        avatar.className = "pn-avatar";
        avatar.style.cssText =
          "display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;font-size:11px;font-weight:600;margin-right:6px;flex-shrink:0;";
        if (avatarUrl) {
          avatar.style.backgroundImage = `url(${avatarUrl})`;
          avatar.style.backgroundSize = "cover";
        } else {
          avatar.style.background = "hsl(var(--primary) / 0.1)";
          avatar.style.color = "hsl(var(--primary))";
          avatar.textContent = (name || "U").charAt(0).toUpperCase();
        }
        btn.style.display = "inline-flex";
        btn.style.alignItems = "center";
        btn.insertBefore(avatar, btn.firstChild);
      });
    }

    injectAvatar();
    const observer = new MutationObserver(injectAvatar);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isAuthenticated]);

  // Listen for signIn / signOut events from plugin class
  useEffect(() => {
    const onSignIn = () => {
      setStep("login");
      setError("");
      setEmail("");
      setPassword("");
      setOtpCode("");
      setOpen(true);
    };
    const onSwitchProject = async () => {
      const auth = getStoredAuth();
      if (!auth?.token) {
        // No session, fall back to full sign-in
        onSignIn();
        return;
      }
      setError("");
      setLoading(true);
      setOpen(true);
      setStep("project");
      try {
        await loadProjects(auth.token);
      } catch {
        setError("Failed to load projects. Please sign in again.");
        setStep("login");
      } finally {
        setLoading(false);
      }
    };
    const onSignOut = () => {
      clearAllStorage();
      restorePlaygroundDialog();
      setLoggedOut();
    };
    window.addEventListener("payonify:sign-in", onSignIn);
    window.addEventListener("payonify:switch-project", onSwitchProject);
    window.addEventListener("payonify:sign-out", onSignOut);
    return () => {
      window.removeEventListener("payonify:sign-in", onSignIn);
      window.removeEventListener("payonify:switch-project", onSwitchProject);
      window.removeEventListener("payonify:sign-out", onSignOut);
    };
  }, [setLoggedOut]);

  // ----- Handlers -----

  const handleLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || !password.trim()) return;
      setLoading(true);
      setError("");

      try {
        const res = await gqlFetch<{
          accounts: {
            login: {
              token?: string;
              otpauthUri?: string;
              user?: { id: string; email: string; firstName: string; lastName: string; avatar?: string };
              errors?: Array<{ key: string; message: string }>;
            };
          };
        }>(LOGIN_MUTATION, { email: email.trim(), password });

        const login = res.data?.accounts?.login;
        if (!login) {
          setError(res.errors?.[0]?.message || "Login failed. Please try again.");
          return;
        }
        if (login.errors?.length) {
          setError(login.errors[0].message);
          return;
        }

        // 2FA required
        if (login.otpauthUri && !login.token) {
          setUserId(login.user?.id || "");
          setStep("otp");
          return;
        }

        // Login successful — fetch projects
        if (login.token && login.user) {
          setStored(STORAGE.AUTH, {
            token: login.token,
            email: login.user.email,
            firstName: login.user.firstName,
            avatar: login.user.avatar,
          });
          await loadProjects(login.token);
        }
      } catch {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [email, password],
  );

  const handleOtp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!otpCode.trim()) return;
      setLoading(true);
      setError("");

      try {
        const res = await gqlFetch<{
          accounts: {
            twoFaLogin: {
              token?: string;
              user?: { id: string; email: string; firstName: string; lastName: string; avatar?: string };
              errors?: Array<{ key: string; message: string }>;
            };
          };
        }>(TWO_FA_LOGIN_MUTATION, { userId, otpCode: otpCode.trim() });

        const login = res.data?.accounts?.twoFaLogin;
        if (!login || !login.token) {
          setError(login?.errors?.[0]?.message || "Invalid OTP code.");
          return;
        }

        setStored(STORAGE.AUTH, {
          token: login.token,
          email: login.user?.email || email,
          firstName: login.user?.firstName || "",
          avatar: login.user?.avatar,
        });
        await loadProjects(login.token);
      } catch {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [otpCode, userId, email],
  );

  async function loadProjects(token: string) {
    const res = await gqlFetch<{
      projects: { stagingProjects: { entries: Project[] } };
    }>(PROJECTS_QUERY, undefined, { Authorization: `Bearer ${token}` });

    const list = res.data?.projects?.stagingProjects?.entries || [];
    if (list.length === 0) {
      setError("No projects found. Create a project in the dashboard first.");
      return;
    }
    setProjects(list);
    setSelectedProject(list[0].identifier);
    setStep("project");
  }

  const handleProjectConnect = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedProject) return;
      setLoading(true);
      setError("");

      const auth = getStoredAuth();
      if (!auth?.token) {
        setError("Session expired. Please sign in again.");
        setStep("login");
        setLoading(false);
        return;
      }

      try {
        const headers = {
          Authorization: `Bearer ${auth.token}`,
          "project-identifier": selectedProject,
        };

        // Fetch publishable key
        const pkRes = await gqlFetch<{
          auth: { publishableKey: { token: string } | null };
        }>(PUBLISHABLE_KEY_QUERY, undefined, headers);

        const pk = pkRes.data?.auth?.publishableKey?.token;
        if (!pk) {
          setError("Could not retrieve API keys for this project.");
          setLoading(false);
          return;
        }

        // Fetch secret key
        const skRes = await gqlFetch<{
          auth: { secretKey: { token: string } | null };
        }>(SECRET_KEY_QUERY, { publishableKey: pk }, headers);

        const sk = skRes.data?.auth?.secretKey?.token;
        if (!sk) {
          setError("Could not retrieve secret key for this project.");
          setLoading(false);
          return;
        }

        // Store keys and project
        const project = projects.find((p) => p.identifier === selectedProject);
        setStored(STORAGE.KEYS, { apiKey: sk, appId: pk });
        setStored(STORAGE.PROJECT, {
          identifier: selectedProject,
          name: project?.name || selectedProject,
        });

        // Update Zudoku auth state
        setLoggedIn({
          profile: {
            sub: auth.email,
            email: auth.email,
            emailVerified: true,
            name: auth.firstName || auth.email,
            pictureUrl: getStoredAuth()?.avatar || undefined,
          },
          providerData: null,
        });

        // Auto-select identity in playground
        try {
          sessionStorage.setItem(
            "identity-storage",
            JSON.stringify({ state: { rememberedIdentity: "payonify-keys" }, version: 0 }),
          );
        } catch {}

        setStep("done");
        setTimeout(() => setOpen(false), 1500);
      } catch {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [selectedProject, projects, setLoggedIn],
  );

  // ----- Close handler -----
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // ----- Render -----

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== "done") handleClose();
      }}
    >
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-background border border-border rounded-xl shadow-xl w-full max-w-md mx-4 p-6 z-10">
        {/* Close button */}
        {step !== "done" && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground text-lg leading-none"
          >
            &times;
          </button>
        )}

        {/* Step 1: Login */}
        {step === "login" && (
          <form onSubmit={handleLogin}>
            <h2 className="text-lg font-semibold mb-1">Sign in to Payonify</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Sign in with your dashboard credentials to auto-populate API keys
              in the playground.
            </p>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2 mb-4">
                {error}
              </div>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full border border-border rounded-md p-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full border border-border rounded-md p-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="mt-5 w-full inline-flex items-center justify-center text-sm font-medium h-10 bg-primary text-primary-foreground hover:opacity-90 transition-all disabled:opacity-50"
              style={{ borderRadius: "var(--radius)" }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <p className="text-xs text-muted-foreground mt-4 text-center">
              Don&apos;t have an account?{" "}
              <a
                href={DASHBOARD_SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Create one
              </a>
            </p>
          </form>
        )}

        {/* Step 2: OTP */}
        {step === "otp" && (
          <form onSubmit={handleOtp}>
            <h2 className="text-lg font-semibold mb-1">
              Two-Factor Authentication
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              Enter the code from your authenticator app.
            </p>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2 mb-4">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">
                OTP Code
              </label>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder="123456"
                required
                maxLength={6}
                autoFocus
                className="w-full border border-border rounded-md p-2.5 bg-background text-sm font-mono text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setStep("login");
                  setError("");
                }}
                className="flex-1 inline-flex items-center justify-center text-sm font-medium h-10 border border-border hover:bg-accent transition-all"
                style={{ borderRadius: "var(--radius)" }}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center text-sm font-medium h-10 bg-primary text-primary-foreground hover:opacity-90 transition-all disabled:opacity-50"
                style={{ borderRadius: "var(--radius)" }}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Project selection */}
        {step === "project" && (
          <form onSubmit={handleProjectConnect}>
            <h2 className="text-lg font-semibold mb-1">Select a Project</h2>
            <p className="text-sm text-muted-foreground mb-5">
              Choose which project&apos;s API keys to use in the playground.
            </p>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2 mb-4">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Project</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full border border-border rounded-md p-2.5 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {projects.map((p) => (
                  <option key={p.identifier} value={p.identifier}>
                    {p.name} ({p.identifier})
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="mt-5 w-full inline-flex items-center justify-center text-sm font-medium h-10 bg-primary text-primary-foreground hover:opacity-90 transition-all disabled:opacity-50"
              style={{ borderRadius: "var(--radius)" }}
            >
              {loading ? "Connecting..." : "Connect"}
            </button>
          </form>
        )}

        {/* Step 4: Done */}
        {step === "done" && (
          <div className="text-center py-4">
            <div className="text-2xl mb-2">&#10003;</div>
            <h2 className="text-lg font-semibold mb-1">Connected</h2>
            <p className="text-sm text-muted-foreground">
              Your API keys are now available in the playground.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SignOut route component
// ---------------------------------------------------------------------------
function SignOutPage() {
  const { logout } = useAuth();

  useEffect(() => {
    logout();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex items-center justify-center py-20">
      <p className="text-muted-foreground">Signing out...</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// API Key Settings page
// ---------------------------------------------------------------------------
function ApiKeySettings() {
  const [tab, setTab] = useState<"connected" | "manual">("connected");
  const [auth, setAuth] = useState<StoredAuth | null>(null);
  const [keys, setKeys] = useState<StoredKeys | null>(null);
  const [project, setProject] = useState<StoredProject | null>(null);

  // Manual entry state
  const [manualApiKey, setManualApiKey] = useState("");
  const [manualAppId, setManualAppId] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setAuth(getStoredAuth());
    setKeys(getStoredKeys());
    setProject(getStoredProject());

    // Pre-fill manual entry
    const storedKeys = getStoredKeys();
    if (storedKeys) {
      setManualApiKey(storedKeys.apiKey);
      setManualAppId(storedKeys.appId);
    }
  }, []);

  const handleManualSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualApiKey.trim() || !manualAppId.trim()) return;
    setStored(STORAGE.KEYS, {
      apiKey: manualApiKey.trim(),
      appId: manualAppId.trim(),
    });
    setKeys({ apiKey: manualApiKey.trim(), appId: manualAppId.trim() });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleManualClear = () => {
    localStorage.removeItem(STORAGE.KEYS);
    setManualApiKey("");
    setManualAppId("");
    setKeys(null);
  };

  const isConnected = auth?.token && keys;

  return (
    <div className="max-w-lg mx-auto py-12 px-6">
      <h1 className="text-2xl font-bold mb-2">API Keys</h1>
      <p className="text-muted-foreground mb-6">
        Manage the API credentials used in the API playground.
      </p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-border">
        <button
          onClick={() => setTab("connected")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "connected"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Dashboard
        </button>
        <button
          onClick={() => setTab("manual")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "manual"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Manual Entry
        </button>
      </div>

      {/* Dashboard tab */}
      {tab === "connected" && (
        <>
          {isConnected ? (
            <div className="space-y-4">
              <div className="bg-accent/50 border border-border rounded-lg p-4">
                <p className="text-sm font-medium mb-1">
                  Connected as {auth.email}
                </p>
                {project && (
                  <p className="text-xs text-muted-foreground">
                    Project: {project.name}
                  </p>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Publishable Key
                  </label>
                  <div className="w-full border border-border rounded-md p-2.5 bg-muted text-sm font-mono text-muted-foreground">
                    {keys.appId}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Secret Key
                  </label>
                  <div className="w-full border border-border rounded-md p-2.5 bg-muted text-sm font-mono text-muted-foreground">
                    {keys.apiKey.slice(0, 12)}{"*".repeat(20)}
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                These keys are automatically used in the API playground.
              </p>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent("payonify:switch-project"),
                    )
                  }
                  className="inline-flex items-center justify-center text-sm font-medium h-10 px-5 border border-border hover:bg-accent transition-all"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  Switch Project
                </button>
                <Link
                  to="/auth/signout"
                  className="inline-flex items-center justify-center text-sm font-medium h-10 px-5 border border-border hover:bg-accent transition-all text-destructive"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  Disconnect
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                Sign in with your Payonify dashboard account to auto-populate
                API keys in the playground.
              </p>
              <button
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("payonify:sign-in"))
                }
                className="inline-flex items-center justify-center text-sm font-medium h-10 px-6 bg-primary text-primary-foreground hover:opacity-90 transition-all"
                style={{ borderRadius: "var(--radius)" }}
              >
                Sign In
              </button>
              <p className="text-xs text-muted-foreground mt-4">
                Don&apos;t have an account?{" "}
                <Link
                  to="/get-started"
                  className="text-primary"
                >
                  Get started
                </Link>
              </p>
            </div>
          )}
        </>
      )}

      {/* Manual Entry tab */}
      {tab === "manual" && (
        <form onSubmit={handleManualSave} className="space-y-4">
          <p className="text-sm text-muted-foreground mb-2">
            Manually enter your API credentials. Keys are stored locally in your
            browser.
          </p>
          <div>
            <label className="block text-sm font-medium mb-1.5">Publishable Key</label>
            <input
              type="text"
              value={manualAppId}
              onChange={(e) => setManualAppId(e.target.value)}
              placeholder="pk_test_..."
              className="w-full border border-border rounded-md p-2.5 bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Secret Key</label>
            <input
              type="password"
              value={manualApiKey}
              onChange={(e) => setManualApiKey(e.target.value)}
              placeholder="sk_test_..."
              className="w-full border border-border rounded-md p-2.5 bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="inline-flex items-center justify-center text-sm font-medium h-10 px-5 bg-primary text-primary-foreground hover:opacity-90 transition-all"
              style={{ borderRadius: "var(--radius)" }}
            >
              {saved ? "Saved!" : "Save"}
            </button>
            {keys && (
              <button
                type="button"
                onClick={handleManualClear}
                className="inline-flex items-center justify-center text-sm font-medium h-10 px-5 border border-border hover:bg-accent transition-all"
                style={{ borderRadius: "var(--radius)" }}
              >
                Clear
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plugin
// ---------------------------------------------------------------------------
export const ApiKeyPlugin: ZudokuPlugin = {
  // ---- AuthenticationPlugin ----
  async signIn() {
    window.dispatchEvent(new CustomEvent("payonify:sign-in"));
  },

  async signOut() {
    window.dispatchEvent(new CustomEvent("payonify:sign-out"));
  },

  async signUp() {
    window.open(DASHBOARD_SIGNUP_URL, "_blank");
  },

  async getAccessToken() {
    return getStoredAuth()?.token || "";
  },

  async signRequest(request: Request) {
    return request;
  },

  onPageLoad() {
    const auth = getStoredAuth();
    const keys = getStoredKeys();
    if (auth?.token && keys) {
      // Fully authenticated — sync profile to Zudoku state
      syncAuthStateToLocalStorage(auth);
    } else {
      // Not fully authenticated — ensure playground login dialog shows
      restorePlaygroundDialog();
    }
  },

  // ---- ApiIdentityPlugin ----
  getIdentities: async (_context: ZudokuContext) => {
    const keys = getStoredKeys();
    if (!keys) return [];

    const project = getStoredProject();
    const label = project?.name
      ? `${project.name} Keys`
      : "Payonify Keys";

    return [
      {
        id: "payonify-keys",
        label,
        authorizeRequest: (request: Request) => {
          // Read fresh from localStorage each time to avoid stale closures
          const freshKeys = getStoredKeys();
          if (freshKeys) {
            const credentials = btoa(`${freshKeys.appId}:${freshKeys.apiKey}`);
            request.headers.set("Authorization", `Basic ${credentials}`);
          }
          return request;
        },
      } satisfies ApiIdentity,
    ];
  },

  // ---- ProfileMenuPlugin ----
  getProfileMenuItems: (_context: ZudokuContext): ProfileNavigationItem[] => [
    { label: "API Keys", path: "/settings/api-keys", category: "top" },
    { label: "Sign Out", path: "/auth/signout", category: "bottom" },
  ],

  // ---- Routes ----
  getRoutes: () => [
    { path: "/settings/api-keys", element: <ApiKeySettings /> },
    { path: "/auth/signout", element: <SignOutPage /> },
  ],
};
